import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Lock, ArrowLeft, Plug } from "lucide-react";
import { api, formatApiError, useSEO } from "@/lib/api";

const field = "w-full bg-[#F5F5F5] rounded-2xl px-5 py-4 text-sm outline-none focus:ring-1 ring-black transition";

export default function AdminPayments() {
  useSEO("Payments Settings");
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "" });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/payments").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const mode = form.razorpay_key_id.startsWith("rzp_live") ? "live" : "test";

  const save = async (e) => {
    e.preventDefault();
    if (!form.razorpay_key_id || !form.razorpay_key_secret) return toast.error("Enter both Key ID and Key Secret.");
    setBusy(true);
    try {
      await api.post("/admin/payments", { ...form, razorpay_mode: mode });
      toast.success("Razorpay connected successfully.");
      setForm({ razorpay_key_id: "", razorpay_key_secret: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/payments/test");
      toast.success(`Connection OK (${data.mode} mode).`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/payments");
      toast.success("Razorpay disconnected.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const connected = settings?.razorpay_enabled;

  return (
    <div data-testid="admin-payments-page" className="pt-32 pb-28">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Link to="/account" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline mb-8">
          <ArrowLeft size={14} /> Back to account
        </Link>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Admin · Payments</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.9] mb-10">Payments</h1>

        {/* Status cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="border border-black/10 rounded-3xl p-6" data-testid="razorpay-status-card">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display text-2xl">Razorpay</span>
              {connected ? (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-black text-white rounded-full px-3 py-1" data-testid="razorpay-connected"><CheckCircle2 size={13} /> Connected</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-[#F5F5F5] rounded-full px-3 py-1" data-testid="razorpay-disconnected"><XCircle size={13} /> Not connected</span>
              )}
            </div>
            {connected ? (
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Key ID: <span className="text-black">{settings.razorpay_key_id}</span></p>
                <p>Secret: {settings.razorpay_secret_masked}</p>
                <p>Mode: <span className="uppercase">{settings.razorpay_mode}</span></p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Connect your Razorpay account below to accept UPI, cards, netbanking and wallets.</p>
            )}
          </div>
          <div className="border border-black/10 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display text-2xl">Stripe</span>
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-black text-white rounded-full px-3 py-1"><CheckCircle2 size={13} /> Active</span>
            </div>
            <p className="text-sm text-muted-foreground">International card checkout is live (test mode).</p>
          </div>
        </div>

        {/* Connect form */}
        <div className="bg-[#F5F5F5] rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <Plug size={18} />
            <h2 className="font-display text-2xl">{connected ? "Update Razorpay keys" : "Connect Razorpay"}</h2>
          </div>
          <form onSubmit={save} className="space-y-4" data-testid="razorpay-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Key ID</label>
              <input data-testid="razorpay-key-id" placeholder="rzp_test_XXXXXXXX or rzp_live_XXXXXXXX"
                value={form.razorpay_key_id} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value.trim() })}
                className={`${field} mt-2 bg-white`} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Key Secret</label>
              <input data-testid="razorpay-key-secret" type="password" placeholder="Your Razorpay key secret"
                value={form.razorpay_key_secret} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value.trim() })}
                className={`${field} mt-2 bg-white`} />
            </div>
            {form.razorpay_key_id && (
              <p className="text-xs text-muted-foreground">Detected mode: <span className="uppercase text-black">{mode}</span></p>
            )}
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Lock size={12} /> Keys are stored securely on your server and never exposed to shoppers. Find them in Razorpay Dashboard → Settings → API Keys.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={busy} data-testid="razorpay-save"
                className="bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors disabled:opacity-60">
                {busy ? "Connecting…" : connected ? "Update keys" : "Connect Razorpay"}
              </button>
              {connected && (
                <>
                  <button type="button" onClick={testConnection} disabled={testing} data-testid="razorpay-test"
                    className="border border-black rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-60">
                    {testing ? "Testing…" : "Test connection"}
                  </button>
                  <button type="button" onClick={disconnect} disabled={busy} data-testid="razorpay-disconnect"
                    className="text-xs uppercase tracking-[0.2em] link-underline">Disconnect</button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
